import {Component, Input} from "@angular/core";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {User} from "../../../shared/api/users/api.models";
import {Role} from "../../../shared/api/roles/api.models";
import {Company} from "../../../shared/api/companies/api.models";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {Api} from "../../../shared/api.service";
import {Me} from "../../../shared/api/me/me.models";
import {Language} from "../../../shared/api/languages/api.models";

@Component({
    selector: 'user-modal',
    template: require('./user.modal.html'),
    styles: [require('./user.modal.scss')]
})

export class UserModal {
    @Input() me: Me;
    @Input() myUserId: number;
    @Input() users: User[];
    @Input() user: User;
    @Input() roles: Role[];
    @Input() companies: Company[];
    @Input() languages: Language[];

    protected settings: any = {
        minPassLength: 8,
        maxPassLength: 255,
        minUsernameLength: 3,
        maxUsernameLength: 50
    };

    protected apiError: string;
    protected passwordValidatorsRequired: boolean;
    protected isDefaultTabValid: boolean = false;
    protected isLanguagesTabValid: boolean = false;

    protected userForm: FormGroup;
    constructor(public activeModal: NgbActiveModal, private fb: FormBuilder, private api: Api) {}

    ngOnInit() {
        if (!this.user.preferences || !this.user.preferences.languages || !this.user.preferences.languages.length) {
            this.user.preferences.languages = [];
        }

        this.userForm = this.fb.group({
            id             : [this.user.id],
            username       : [
                { value: this.user.username, disabled: this.user.id },
                [
                    Validators.required,
                    Validators.minLength(this.settings.minUsernameLength),
                    Validators.maxLength(this.settings.maxUsernameLength),
                    Validators.pattern(/^[0-9a-zA-Z]+$/),
                    (input: FormControl) => {
                        if (!input.value) {
                            return null;
                        }

                        let found = false;

                        for (let i = 0; i < this.users.length; i++) {
                            if (
                                this.users[i].username.toLowerCase() === input.value.toLowerCase() &&
                                this.users[i].id !== this.user.id
                            ) {
                                found = true;
                                break;
                            }
                        }

                        if (found === true) {
                            return { nametaken: true };
                        }

                        return null;
                    }
                ]
            ],
            password       : [null, this.getPasswordValidators()],
            passwordRepeat : [null, this.getPasswordRepeatValidators()],
            roleId         : [
                {value : this.user.roleId, disabled: this.myUserId === this.user.id},
                [Validators.required]
            ],
            companyId      : [this.user.companyId, [Validators.required]],
            preferences    : this.fb.group({
                languages: [this.user.preferences.languages, Validators.required]
            })
        });

        this.passwordValidatorsRequired = !this.user.id;
        this.setValidationFlags();

        this.userForm.valueChanges.subscribe((user: User) => {
            Object.assign(this.user, user);

            this.setValidationFlags();

            if (this.user.password === '') {
                this.user.password = null;
            }

            // Determine wheter password information is required or not
            let forceRequired = (
                !this.userForm.controls['password'].value &&
                !this.userForm.controls['passwordRepeat'].value
            ) === false;

            if (this.user.id && this.passwordValidatorsRequired !== forceRequired) {
                this.passwordValidatorsRequired = forceRequired;

                this.userForm.controls['password'].setValidators(this.getPasswordValidators(forceRequired));
                this.userForm.controls['passwordRepeat'].setValidators(this.getPasswordRepeatValidators(forceRequired));
                this.userForm.controls['password'].updateValueAndValidity();
                this.userForm.controls['passwordRepeat'].updateValueAndValidity();
            }
        });
    }

    toggleLanguage(language: Language): void {
        const control = this.userForm.controls['preferences']['controls'].languages as FormControl;
        let languages = control.value;
        const index   = languages.indexOf(language.id);

        if (index === -1) {
            languages.push(language.id);
        } else {
            languages.splice(index, 1);
        }

        control.setValue(languages);
    }

    save() {
        this.apiError = null;

        if (this.user.id) {
            this.api.users().update(this.user).subscribe(
                (user: User) => {
                    this.activeModal.close(user);
                },
                (error: any) => {
                    if (error.errors) {
                        this.apiError = error.errors[0].detail;
                    } else {
                        this.apiError = error.error.message;
                    }
                }
            );

            return;
        }

        this.api.users().store(this.user).subscribe(
            (user: User) => {
                this.activeModal.close(user);
            },
            (error: any) => {
                if (error.errors) {
                    if (error.errors[0].title === 'already_exists') {
                        let control = this.userForm.controls['username'] as FormControl;
                        control.setErrors({'nametaken' : true});
                    } else {
                        this.apiError = error.errors[0].detail;
                    }
                } else {
                    this.apiError = error.error.message;
                }
            }
        )
    }

    private setValidationFlags() {
        this.isDefaultTabValid = (
            this.userForm.controls.username.invalid ||
            this.userForm.controls.password.invalid ||
            this.userForm.controls.passwordRepeat.invalid ||
            this.userForm.controls.companyId.invalid
        ) === false;

        this.isLanguagesTabValid = !this.userForm.controls['preferences']['controls'].languages.invalid;

    }

    private getPasswordValidators(forceRequired: boolean = false) {
        if (this.user.id && forceRequired === false) {
            return [];
        }

        return [
            Validators.required,
            Validators.minLength(this.settings.minPassLength),
            Validators.maxLength(this.settings.maxPassLength)
        ];
    }

    private getPasswordRepeatValidators(forceRequired: boolean = false) {
        if (this.user.id && forceRequired === false) {
            return [];
        }

        return [
            Validators.required,
            Validators.minLength(this.settings.minPassLength),
            Validators.maxLength(this.settings.maxPassLength),
            (input: FormControl) => {

                if (input.parent && input.value !== input.parent.controls['password'].value) {
                    return { nomatch: true };
                }

                return null;
            }
        ];
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/settings/users/components/user.modal.ts